import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui';
import CheckIn from '../components/todos/CheckIn';
import PushOptIn from '../components/todos/PushOptIn';
import SweepCards from '../components/todos/SweepCards';
import TodoList, { type ClosedTodos } from '../components/todos/TodoList';
import { watchdogLine, wordsOf } from '../components/todos/todoFormat';
import { api, type MyTodos } from '../lib/api';

const NOT_LINKED = 'Your app login does not match a GoHighLevel user by email, so there are no tasks to show.';
const NOT_READ = "Couldn't read your tasks - try again.";
/** ⭐ R4 / R13: a copy older than this may still show work he finished in GoHighLevel as open. */
const STALE_MINUTES = 30;

const staleLine = (age: number | null): string | null => {
  if (age === null) return 'This list may be out of date - not refreshed yet';
  return age > STALE_MINUTES ? `This list may be out of date - last refreshed ${age} minutes ago` : null;
};

/**
 * TODO1 - the To-dos page (spec 4.1): his GoHighLevel tasks from Call Intelligence and the unassigned ones, so none is
 * lost. Until his first sweep is finished it leads with the clean-up - and ⭐ R31: his whole open list ALWAYS follows,
 * because a page that hid it until he finished or skipped the clean-up would be the lost to-do this exists to prevent.
 * ⛔ Loading the page only READS. Every write - Done, Drop, Undo, Later, the sweep, reminders - is a tap.
 */
export default function Todos() {
  const [data, setData] = useState<MyTodos | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Bumped on every good read. The list and the sweep remount on it, so what a tap left behind ("Marked done. Undo")
  // lasts until the next read of the list and no longer.
  const [reads, setReads] = useState(0);
  // ⭐ R33: what a list row's Done or Drop closed since the last read - here, so the clean-up's cards skip it too.
  const [closed, setClosed] = useState<ClosedTodos>({});
  const onClosed = useCallback((id: string, how: 'done' | 'dropped' | null) => {
    setClosed(({ [id]: _was, ...rest }) => (how ? { ...rest, [id]: how } : rest));
  }, []);
  // ⭐ R7: the check-in opens where a reminder lands (`?checkin=1`, pushService's url) or from "Check in" - in the url
  // either way, so Back closes it.
  const [params, setParams] = useSearchParams();
  const checkingIn = params.get('checkin') === '1';
  const setCheckIn = (open: boolean) => setParams((p) => {
    const next = new URLSearchParams(p);
    if (open) next.set('checkin', '1');
    else next.delete('checkin');
    return next;
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.todos.list());
      setReads((n) => n + 1);
      setClosed({});
      setLoadError(null);
    } catch (e) {
      setLoadError(wordsOf(e, NOT_READ));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const linked = data?.linked === true ? data : null;
  const stale = linked ? staleLine(linked.copyAgeMinutes) : null;
  // Task 10: calls Call Intelligence has not summarized - the Mac Mini may be off. No dismiss: it goes when they are.
  const watchdog = linked?.watchdog ? watchdogLine(linked.watchdog.missing, linked.watchdog.since, new Date()) : null;

  return (
    <Layout>
      <div className="mx-auto max-w-3xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">
              Tasks
            </h1>
            {linked && (
              <p className="mt-1 text-sm text-gray-500">
                From your calls, grouped by due day. Done and Drop close the task in GoHighLevel and stay below for a week, with Reopen.
              </p>
            )}
          </div>
          {linked && (
            <div className="flex shrink-0 flex-wrap justify-end gap-2">
              {!checkingIn && <Button variant="outline" onClick={() => setCheckIn(true)}>Check in</Button>}
              <Button variant="outline" onClick={() => void load()} isLoading={loading}>Refresh</Button>
            </div>
          )}
        </div>

        {watchdog && (
          <p className="mb-4 rounded-lg border border-warning/30 bg-warning-wash px-4 py-2 text-sm text-warning">{watchdog}</p>
        )}
        {stale && (
          <p className="mb-4 rounded-lg border border-warning/30 bg-warning-wash px-4 py-2 text-sm text-warning">{stale}</p>
        )}
        {loadError && (
          <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>{loadError}</span>
            <Button size="sm" variant="outline" onClick={() => void load()} isLoading={loading}>Try again</Button>
          </div>
        )}
        {!data && !loadError && <p className="py-16 text-center text-gray-500">Loading...</p>}
        {data && !data.linked && <p className="py-10 text-sm text-gray-700">{NOT_LINKED}</p>}

        {linked && (
          <>
            {/* Not keyed by the reads: his conversation survives the re-read after every answer. */}
            {checkingIn && (
              <CheckIn
                todos={linked.todos}
                closed={closed}
                onClosed={onClosed}
                onChanged={load}
                onClose={() => setCheckIn(false)}
              />
            )}
            <PushOptIn sweepFinished={linked.sweep.finished} />
            {!linked.sweep.finished && (
              <>
                <SweepCards key={reads} sweep={linked.sweep} onChanged={load} closed={closed} />
                {/* Its own heading, so the clean-up's cards and the list are told apart. */}
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">All open tasks</h2>
              </>
            )}
            <TodoList key={reads} todos={linked.todos} finished={linked.closed} onReopened={() => void load()} closed={closed} onClosed={onClosed} />
            {linked.olderCount > 0 && (
              <p className="mt-6 text-sm text-gray-500">{`Older than 30 days: ${linked.olderCount}`}</p>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
